import React, { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from '@emotion/styled'

import Box from '../components/Box'
import GrooverText from '../components/GrooverText'
import PrivateLessonsReceipt from '../components/PrivateLessonsReceipt'
import PrivateLessonsLanguageToggle from '../components/PrivateLessonsLanguageToggle'
import useAppState from '../hooks/useAppState'
import { Language } from '../../../pure-js/types/Antiloop'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { resolvePrivateLessonsPortalData } from '../utils/privateLessonsPortalHelpers'

export const PrivateLessonsPortalPage: React.FC = () => {
  const { registerToken } = useParams<{ registerToken: string }>()
  const { state } = useAppState()
  const language: Language = state.selectedLanguage

  const data = useMemo(() => resolvePrivateLessonsPortalData(registerToken), [registerToken])

  const locale = language === 'sv' ? 'sv-SE' : language === 'no' ? 'nb-NO' : 'en-US'

  const formatTextWithPlaceholders = (text: string, params: Array<string | number>) => {
    let formatted = text
    params.forEach((param, i) => {
      formatted = formatted.replaceAll(`{${i}}`, param.toString())
    })
    return formatted
  }

  if (!data) {
    return (
      <Page backgroundColor="white" fullWidth>
        <Container fullWidth>
          <HeaderRow fullWidth>
            <HeaderTopRow direction="row" justify="space-between" align="flex-start" fullWidth>
              <TitleWithLanguage direction="row" gap="16px" align="center">
                <PageTitle>
                  <GrooverText textKey="privateLessonsPortalTitle" language={language} />
                </PageTitle>
                <PrivateLessonsLanguageToggle />
              </TitleWithLanguage>
            </HeaderTopRow>
          </HeaderRow>

          <UnavailableCard>
            <UnavailableTitle>
              <GrooverText textKey="privateLessonsPortalUnavailableTitle" language={language} />
            </UnavailableTitle>
            <UnavailableDescription>
              <GrooverText textKey="privateLessonsPortalUnavailableDescription" language={language} />
            </UnavailableDescription>
            <PortalLoginLink to={buildRoute(RoutePath.PORTAL_LOGIN)}>
              <GrooverText textKey="privateLessonsCheckoutOpenPortalLink" language={language} />
            </PortalLoginLink>
          </UnavailableCard>
        </Container>
      </Page>
    )
  }

  return (
    <Page backgroundColor="white" fullWidth>
      <Container fullWidth>
        <HeaderRow fullWidth>
          <HeaderTopRow direction="row" justify="space-between" align="flex-start" fullWidth>
            <TitleWithLanguage direction="row" gap="16px" align="center">
              <PageTitle>
                <GrooverText textKey="privateLessonsPortalTitle" language={language} />
              </PageTitle>
              <PrivateLessonsLanguageToggle />
            </TitleWithLanguage>

            <ClientMeta>
              <ClientName>{data.clientName}</ClientName>
              <ClientLink href={data.clientURL} target="_blank" rel="noreferrer">
                {data.clientURL}
              </ClientLink>
            </ClientMeta>
          </HeaderTopRow>
        </HeaderRow>

        <Content direction="row" gap="32px" fullWidth changeDirectionOnMobile>
          <LeftColumn fullWidth>
            <Card>
              <CardTitle>
                <GrooverText textKey="privateLessonsPortalScheduledSessions" language={language} />
              </CardTitle>

              {data.scheduledSessions.length === 0 ? (
                <EmptyState>
                  <GrooverText textKey="privateLessonsPortalNoScheduledSessions" language={language} />
                </EmptyState>
              ) : (
                <SessionsList fullWidth gap="16px">
                  {data.scheduledSessions.map((s) => (
                    <SessionRow key={s.id} fullWidth direction="row" justify="space-between" align="flex-start">
                      <SessionContent>
                        <SessionDate>{new Date(s.startIso).toLocaleString(locale)}</SessionDate>
                        <SessionDetails direction="row" gap="24px" align="center">
                          <DetailItem direction="row" gap="6px" align="center">
                            <DetailLabel>
                              <GrooverText textKey="privateLessonsPortalSessionInstructor" language={language} />
                            </DetailLabel>
                            <DetailValue>{s.instructorName}</DetailValue>
                          </DetailItem>
                          <DetailItem direction="row" gap="6px" align="center">
                            <DetailLabel>
                              <GrooverText textKey="privateLessonsPortalSessionLocation" language={language} />
                            </DetailLabel>
                            <DetailValue>{s.locationName}</DetailValue>
                          </DetailItem>
                        </SessionDetails>
                        {s.notes && <SessionNotes>{s.notes}</SessionNotes>}
                      </SessionContent>

                      <DurationPill>
                        {formatTextWithPlaceholders(getGrooverText('privateLessonsPortalSessionDuration', language), [
                          s.durationMin
                        ])}
                      </DurationPill>
                    </SessionRow>
                  ))}
                </SessionsList>
              )}
            </Card>
          </LeftColumn>

          <RightColumn fullWidth>
            <PrivateLessonsReceipt receipt={data.receipt} language={language} />
          </RightColumn>
        </Content>
      </Container>
    </Page>
  )
}

const Page = styled(Box)`
  min-height: 100vh;
  background: #fff;
`

const Container = styled(Box)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`

const HeaderRow = styled(Box)`
  margin-bottom: 24px;
`

const HeaderTopRow = styled(Box)`
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`

const TitleWithLanguage = styled(Box)`
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #000;
  margin: 0;
`

const ClientMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left;
    width: 100%;
  }
`

const ClientName = styled.div`
  color: #000;
  font-weight: 600;
  font-size: 16px;
`

const ClientLink = styled.a`
  color: #7c3aed;
  text-decoration: none;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`

const Content = styled(Box)``

const LeftColumn = styled(Box)`
  flex: 1;
  min-width: 0;
`

const RightColumn = styled(Box)`
  flex: 1;
  min-width: 0;
`

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin: 0 0 20px 0;
`

const EmptyState = styled.div`
  color: #666;
  font-size: 14px;
  padding: 16px 0;
`

const SessionsList = styled(Box)``

const SessionRow = styled(Box)`
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`

const SessionContent = styled.div`
  flex: 1;
  min-width: 0;
`

const SessionDate = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 8px;
`

const SessionDetails = styled(Box)`
  flex-wrap: wrap;
  gap: 8px 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`

const DetailItem = styled(Box)``

const DetailLabel = styled.span`
  color: #666;
  font-size: 14px;
`

const DetailValue = styled.span`
  color: #000;
  font-weight: 600;
  font-size: 14px;
`

const SessionNotes = styled.div`
  margin-top: 8px;
  color: #666;
  font-size: 13px;
  font-style: italic;
`

const DurationPill = styled.div`
  background: #7c3aed;
  color: #fff;
  border-radius: 999px;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 768px) {
    align-self: flex-end;
  }
`

const UnavailableCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  max-width: 560px;
`

const UnavailableTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin: 0 0 12px 0;
`

const UnavailableDescription = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 20px 0;
  line-height: 1.5;
`

const PortalLoginLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #7c3aed;
  color: #fff;
  text-decoration: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: 600;
  font-size: 14px;

  &:hover {
    background: #6d28d9;
  }
`

export default PrivateLessonsPortalPage
